import { ReactNode } from "react";
import { FormInstance, TabPaneProps } from "antd";
import { Dayjs } from "dayjs";
import { ProColumns } from "@ant-design/pro-table";
import { DeliveryTypeEnum } from "@/components/IncomingShipments/types";
import { StepperStepsProps } from "@/components/Stepper/types";
import { NotificationPriorityEnum } from "@/pages/ApplicationConfig/NotificationsConfiguration/types";
import { DataTablePermissionsMap } from "@/components/DataTable/types";
import { Guid } from "js-guid";

export enum StatusEnum {
	SUCCESS = "success",
	ERROR = "error"
}

export enum PermissionType {
	READ = "Read",
	WRITE = "Write",
	DELETE = "Delete"
}

export enum PermissionTypeEnum {
	Read = 1,
	Write = 2,
	Delete = 3,
	Import = 4
}

export enum AccessType {
	READ = "Read",
	GRANT = "GrantAccess",
	REVOKE = "RevokeAccess",
	MANAGE = "ManageAccess"
}

export enum CreateSelectType {
	EXISTING = 1,
	NEW = 2
}

export type AccessTypeHierarchy = {
	[AccessType.READ]: 1;
	[AccessType.GRANT]: 2;
	[AccessType.REVOKE]: 3;
	[AccessType.MANAGE]: 4;
};

export enum AccessControlEnum {
	ROLE_BASED = "role",
	PERMISSION_BASED = "permission"
}

export type LoggedUser = {
	id: string;
	username: string;
	fullName: string;
	birthdate: Dayjs | null;
	roles: Array<string>;
	email: string;
	phoneNumber: string;
	applicationVersion: string;
	workplaceTitle: string;
	employeeId: string;
	branchOfficeId?: string;
};

export type UserProfile = {
	selectedLanguage: string;
	mailbox?: SelectItemDto | null;
	defaultClass?: SelectItemDto | null;
	defaultOrganizationalUnit?: SelectItemDto | null;
	defaultWorkplaceEmployee?: SelectItemDto | null;
	defaultIncomingShipmentStatus?: SelectItemDto | null;
	defaultOutgoingShipmentStatus?: SelectItemDto | null;
	defaultPleadingStatus?: SelectItemDto | null;
	defaultCaseStatus?: SelectItemDto | null;
	defaultDeliveryTypes?: DeliveryTypeEnum[];
};

export type ApplicationConfigDto = {
	activeSystematizationExists: boolean;
	caseStatuses: CaseInternalStatuses;
	displayAuditProperties: boolean;
	pleadingStatuses: PleadingInternalStatuses;
	enabledFeatures: Array<string> | null;
	priorityColorCodes: { priority: NotificationPriorityEnum; hexColorCode: string }[];
};

export type CaseInternalStatuses = {
	closedStatusId: string;
	openedStatusId: string;
	inProgressStatusId: string;
	canceledStatusId: string;
	archivedStatusId: string;
	issuedForInspectionStatusId: string;
};

export type PleadingInternalStatuses = {
	closedStatusId: string;
	openedStatusId: string;
	inProgressStatusId: string;
	canceledStatusId: string;
	approvalRequestedStatusId: string;
	approvalRejectedStatusId: string;
	approvalGrantedStatusId: string;
	dispatchedStatusId: string;
	dispatchReturnedStatusId: string;
	readyForDispatchStatusId: string;
};

export type BaseEditFormInitialState = {
	id: string;
	modalProps?: any;
};

export type LayoutSpan = {
	span: number;
};

export type FormLayout = {
	labelCol: LayoutSpan;
	wrapperCol: LayoutSpan;
};

export interface SectionProps {
	controllerName: string;
	permissionName: string;
	permissionMap: DataTablePermissionsMap;
	createEndpoint?: string;
	updateEndpoint?: string;
	detailsNavigateUrl?: string;
	detailsNavigateOnAdd?: boolean | null;
	openEditFormAfterCreation?: boolean;
	editDrawerFormLayout?: FormLayout;
	hideDefaultRowButtons?: boolean;
	disableCreate?: boolean;
	disableCreateOnRowCount?: (rowCount: number) => boolean;
	disableUpdate?: boolean;
	hideGroupDeleteAction?: boolean;
	hideInfoButton?: boolean;
	hideWizardButton?: boolean;
	executeCustomSave?: (preSaveItem: any, createEndpointOverride: string | null, updateEndpointOverride: string | null) => Promise<any>;
	getFormItems?: (
		formInstance: FormInstance,
		editState: BaseEditFormInitialState,
		editProperties: Array<DtoProperty>,
		setEditState?: (state: BaseEditFormInitialState) => void,
		onFormSetFieldsValueHandler?: (values: any) => void
	) => ReactNode;
	getEditFormInitialState?: (editObject: any) => any;
	getDefaultPreEditItem?: (editObject: BaseDetailsDto) => BaseDetailsDto;
	getPreEditItem?: (editObject: any) => Promise<any> | any;
	getPreSaveItem?: (submittedObject: Readonly<any>, editObject?: any) => any;
	getListFixedFilterParams?: () => FilterElement[];
	getColumnDefinition?: (property: DtoProperty) => ProColumns;
	getCustomButtons?: (record: any, _callback, type) => ReactNode[];
	getCustomRowButtons?: (record: any, _callback?: () => void, buttonType?: ReactNode) => ReactNode[];
	getCustomDrawerButtons?: (record: any, _callback?: () => void, buttonType?: ReactNode) => ReactNode[];
	getCustomFilterFunction?: (requestParam: FilterRequest) => Promise<FilterResponse<any>>;
	hideUpdateButton?: (item: any) => boolean;
	hideDeleteButton?: (item: any) => boolean;
	editFormValuesChangedHandler?: (
		formInstance: FormInstance,
		editState: BaseEditFormInitialState,
		setEditState: (state: BaseEditFormInitialState) => void,
		changedValues: any,
		allValues: any
	) => void;
	getCustomSelectionToolbarButtons?: (selectedRows: any[], onCleanSelected: () => void, reloadAction: any) => ReactNode[];
	wizardProps?: StepperStepsProps;
	assignmentModalProps?: any;
	workflowModalProps?: any;
	userAccessModalConfig?: any;
	getCustomToolbarButtons?: () => ReactNode[];
	disableRowSelection?: boolean;
	expandedRowRenderFunction?: (record: any) => any;
	expandedRowRenderCondition?: (record: any) => true;
	defaultExpandAllRows?: boolean;
	accessModalProps?: any;
	entityPropertiesAssignable?: boolean;
	fetchDefaultValuesForCreateFromServer?: boolean;
	customReadPermissionCheck?: () => boolean;
	customCreatePermissionCheck?: () => boolean;
	customUpdatePermissionCheck?: () => boolean;
	customDeletePermissionCheck?: () => boolean;
}

export type DetailsTab = {
	tabPaneProps: TabPaneProps & { key: string };
	Component: React.ComponentType<any>;
	permissionName?: string;
};

export enum CustomPropertyType {
	Text = 1,
	Number = 2,
	DateTime = 3,
	Bool = 4,
	Select = 5,
	FixedSelectRelation = 6,
	SearchSelectRelation = 7
}

export enum SortOrder {
	ASCENDING = 0,
	DESCENDING = 1
}

export type Pagination = {
	currentPage: number;
	pageSize: number;
};

export enum AccessRightEnum {
	NoAccess = 0,
	Read = 1,
	Write = 2,
	Delete = 3,
	Import = 4
}

export type AccessRight = {
	entityId: string;
	accessRight: PermissionTypeEnum;
};

export type SortElement = {
	propertyName: string;
	sortOrder: SortOrder;
};

export type CustomPropertyValueFilterElement = {
	customPropertyDefinitionId: string;
	customPropertyType: CustomPropertyType;
	value: string | undefined;
	minValue?: string | null;
	maxValue?: string | null;
	customPropertyEntityType?: string | null;
};

export interface CustomPropertyValueFilterElements {
	customPropertyValueFilterElements: CustomPropertyValueFilterElement[];
}

export type FilterElement = {
	propertyName: string;
	propertyType: number;
	value?: string | undefined;
	minValue?: Dayjs | string | number | null;
	maxValue?: Dayjs | string | number | null;
};

export type FilterGroup = {
	filterElements: FilterElement[];
};

export interface FilterRequest extends Pagination {
	sortElements?: SortElement[];
	customPropertyValuesFilter?: CustomPropertyValueFilterElements;
	filterGroups?: FilterGroup[];
}

export interface SearchContentRequest extends Pagination {
	keyword: string;
}

export enum PropertyType {
	Text = 1,
	Integer = 2,
	Decimal = 3,
	DateTime = 4,
	Bool = 5,
	Enum = 6,
	FixedSelect = 7,
	SearchSelect = 8,
	ComplexProperty = 9
}

export type EditItem = {
	id: string;
};

export type PermissionDto = {
	permissionId: string;
	permissionName: string | null;
	permissionTranslation: string | null;
	granted: boolean;
};

export type PermissionGroupDto = {
	groupId: string;
	granted: boolean;
	groupName: string | null;
	groupTranslation: string | null;
	subgroups: Array<PermissionSubgroupDto> | null;
};

export type PermissionSubgroupDto = {
	subgroupId: string;
	subgroupName: string | null;
	subgroupTranslation: string | null;
	granted: boolean;
	permissions: Array<PermissionDto> | null;
};

export type LoginRequest = {
	username: string;
	password: string;
};

export type ChangePasswordRequest = {
	oldPassword: string;
	newPassword: string;
};

export type OverridePasswordRequest = {
	userIds: Array<string>;
	newPassword: string;
};

export type ImpersonateBranchOfficeRequest = {
	originalUsername: string;
	branchOfficeId: string;
};

export type ImpersonateEmployeeRequest = {
	originalUsername: string;
	employeeId: string;
};

export enum AuditEntrySortEnum {
	ByDate = 0,
	ByUser = 1,
	ByChangeType = 2
}

export type AuditEntrySortMapping = {
	[key: string]: AuditEntrySortEnum;
};

export enum ChangeTypeEnum {
	Created = 1,
	Modified = 2,
	Deleted = 3,
	Restored = 4,
	StatusChanged = 5,
	Activated = 6,
	Deactivated = 7
}

export interface FilterResponse<T> extends Pagination {
	items: Array<T>;
	accessRights?: AccessRight[];
	totalCount: number;
}

export interface AuditFilterParam extends Pagination {
	entityId: string;
	subject: string;
	changeType: ChangeTypeEnum;
	changeMadeByUserId: string;
	changesFrom: string;
	changesTo: string;
	changeDescription: string;
	sortBy: AuditEntrySortEnum;
	sortAscending: boolean;
}

export type AuditFilterResponse = {
	entityId: string;
	subject: string;
	changeDate: string;
	changeType: ChangeTypeEnum;
	changeDescription: string;
	changedByUsername: string;
	rowId?: Guid;
};

export type BaseDetailsDto = {
	id: string;
	createdDate: Dayjs | string;
	modifiedDate: Dayjs | string;
	deletedDate?: Dayjs | string;
	createdByUserId?: Dayjs | string;
	modifiedByUserId?: Dayjs | string;
	deletedByUserId?: Dayjs | string;
	concurrencyStamp?: Dayjs | string;
};

export type SelectItemDto = {
	value: any;
	label: string;
};

export interface SelectItemWithIsDefaultDto extends SelectItemDto {
	isDefault: boolean;
}

export interface AdditionalFilter {
	propertyName: string;
	propertyValue: any;
}

export enum TextType {
	NotApplicable = 0,
	SingleLine = 1,
	MultiLine = 2,
	RichText = 3
}

export enum DateTimeType {
	NotApplicable = 0,
	DateOnly = 1,
	TimeOnly = 2,
	DateTime = 3
}

export interface TypedDtoProperty<T> {
	type: PropertyType;
	name: string;
	displayName: string;
	customPropertyDefinitionId?: string | null;
	isSearchable: boolean;
	isRequired: boolean;
	enableQuickFilter: boolean;
	quickFilterOrder: number;
	uniqueEndpointPath: string;
	defaultValue?: T | null;
	disableSort: boolean;
	gridColumnOrder: number;
	customPropertyEntityType?: string;
}

export interface BoolDtoProperty extends TypedDtoProperty<boolean> {
	type: PropertyType.Bool;
}

export interface DateTimeDtoProperty extends TypedDtoProperty<string> {
	type: PropertyType.DateTime;
	dateOnly: boolean;
	timeOnly: boolean;
	minValue?: string | null;
	maxValue?: string | null;
}

export interface DecimalDtoProperty extends TypedDtoProperty<number> {
	type: PropertyType.Decimal;
	minValue?: number | null;
	maxValue?: number | null;
	precision: number;
}

export interface NumberDtoProperty extends TypedDtoProperty<number> {
	type: PropertyType.Integer;
	minValue?: number;
	maxValue?: number;
}
export interface EnumDtoProperty extends TypedDtoProperty<number> {
	type: PropertyType.Enum;
	values: Array<SelectItemDto>;
	allowMultipleValues?: boolean;
}

export interface SelectDtoProperty extends TypedDtoProperty<string> {
	type: PropertyType.FixedSelect;
	values: Array<SelectItemDto>;
	allowMultipleValues?: boolean;
}

export interface SearchSelectDto extends TypedDtoProperty<string> {
	type: PropertyType.SearchSelect;
	endpoint: string;
	filterPropertyName?: string;
	defaultLabel?: string;
	httpMethod?: string;
	allowMultipleValues?: boolean;
}

export interface TextDtoProperty extends TypedDtoProperty<string> {
	type: PropertyType.Text;
	isMultiline: boolean;
	isRichText: boolean;
	minLength?: number;
	maxLength?: number;
	isEmailAddress: boolean;
}

export type DtoProperty =
	| BoolDtoProperty
	| DateTimeDtoProperty
	| DecimalDtoProperty
	| EnumDtoProperty
	| SelectDtoProperty
	| NumberDtoProperty
	| SearchSelectDto
	| TextDtoProperty;

export type NumberSeparators = {
	thousands: string;
	decimalPoint: string;
};

export enum FileKeyEnum {
	New = "new",
	Existing = "existing"
}

export enum FileOcrStatusEnum {
	DontOcr = 0,
	OcrPending = 1,
	OcrSuccessful = 2,
	OcrFailed = 3
}

export type FileContent = BaseDetailsDto & {
	tenantId: string;
	name?: string;
	versionNumber: number;
	content?: string;
	fileType?: string;
	filePath?: string;
	ocrStatus: FileOcrStatusEnum;
};

export type CustomPropertyValue = {
	customPropertyDefinitionId: string;
	value: any;
};

export type FileResponse = { content: any; fileName: string; fileType?: string };

export enum IsoCountryCodeEnum {
	//"Andorra"
	AD = 1,
	//"United Arab Emirates"
	AE = 2,
	//"Afghanistan"
	AF = 3,
	//"Antigua and Barbuda"
	AG = 4,
	//"Anguilla"
	AI = 5,
	//"Albania"
	AL = 6,
	//"Armenia"
	AM = 7,
	//"Angola"
	AO = 8,
	//"Antarctica"
	AQ = 9,
	//"Argentina"
	AR = 10,
	//"American Samoa"
	AS = 11,
	//"Austria"
	AT = 12,
	//"Australia"
	AU = 13,
	//"Aruba"
	AW = 14,
	//"Åland Islands"
	AX = 15,
	//"Azerbaijan"
	AZ = 16,
	//"Bosnia and Herzegovina"
	BA = 17,
	//"Barbados"
	BB = 18,
	//"Bangladesh"
	BD = 19,
	//"Belgium"
	BE = 20,
	//"Burkina Faso"
	BF = 21,
	//"Bulgaria"
	BG = 22,
	//"Bahrain"
	BH = 23,
	//"Burundi"
	BI = 24,
	//"Benin"
	BJ = 25,
	//"Saint Barthélemy"
	BL = 26,
	//"Bermuda"
	BM = 27,
	//"Brunei Darussalam"
	BN = 28,
	//"Bolivia, Plurinational State of"
	BO = 29,
	//"Bonaire, Sint Eustatius and Saba"
	BQ = 30,
	//"Brazil"
	BR = 31,
	//"Bahamas"
	BS = 32,
	//"Bhutan"
	BT = 33,
	//"Bouvet Island"
	BV = 34,
	//"Botswana"
	BW = 35,
	//"Belarus"
	BY = 36,
	//"Belize"
	BZ = 37,
	//"Canada"
	CA = 38,
	//"Cocos (Keeling) Islands"
	CC = 39,
	//"Congo, Democratic Republic of the"
	CD = 40,
	//"Central African Republic"
	CF = 41,
	//"Congo"
	CG = 42,
	//"Switzerland"
	CH = 43,
	//"Côte d'Ivoire"
	CI = 44,
	//"Cook Islands"
	CK = 45,
	//"Chile"
	CL = 46,
	//"Cameroon"
	CM = 47,
	//"China"
	CN = 48,
	//"Colombia"
	CO = 49,
	//"Costa Rica"
	CR = 50,
	//"Cuba"
	CU = 51,
	//"Cabo Verde"
	CV = 52,
	//"Curaçao"
	CW = 53,
	//"Christmas Island"
	CX = 54,
	//"Cyprus"
	CY = 55,
	//"Czechia"
	CZ = 56,
	//"Germany"
	DE = 57,
	//"Djibouti"
	DJ = 58,
	//"Denmark"
	DK = 59,
	//"Dominica"
	DM = 60,
	//"Dominican Republic"
	DO = 61,
	//"Algeria"
	DZ = 62,
	//"Ecuador"
	EC = 63,
	//"Estonia"
	EE = 64,
	//"Egypt"
	EG = 65,
	//"Western Sahara"
	EH = 66,
	//"Eritrea"
	ER = 67,
	//"Spain"
	ES = 68,
	//"Ethiopia"
	ET = 69,
	//"Finland"
	FI = 70,
	//"Fiji"
	FJ = 71,
	//"Falkland Islands (Malvinas)"
	FK = 72,
	//"Micronesia, Federated States of"
	FM = 73,
	//"Faroe Islands"
	FO = 74,
	//"France"
	FR = 75,
	//"Gabon"
	GA = 76,
	//"United Kingdom of Great Britain and Northern Ireland"
	GB = 77,
	//"Grenada"
	GD = 78,
	//"Georgia"
	GE = 79,
	//"French Guiana"
	GF = 80,
	//"Guernsey"
	GG = 81,
	//"Ghana"
	GH = 82,
	//"Gibraltar"
	GI = 83,
	//"Greenland"
	GL = 84,
	//"Gambia"
	GM = 85,
	//"Guinea"
	GN = 86,
	//"Guadeloupe"
	GP = 87,
	//"Equatorial Guinea"
	GQ = 88,
	//"Greece"
	GR = 89,
	//"South Georgia and the South Sandwich Islands"
	GS = 90,
	//"Guatemala"
	GT = 91,
	//"Guam"
	GU = 92,
	//"Guinea-Bissau"
	GW = 93,
	//"Guyana"
	GY = 94,
	//"Hong Kong"
	HK = 95,
	//"Heard Island and McDonald Islands"
	HM = 96,
	//"Honduras"
	HN = 97,
	//"Croatia"
	HR = 98,
	//"Haiti"
	HT = 99,
	//"Hungary"
	HU = 100,
	//"Indonesia"
	ID = 101,
	//"Ireland"
	IE = 102,
	//"Israel"
	IL = 103,
	//"Isle of Man"
	IM = 104,
	//"India"
	IN = 105,
	//"British Indian Ocean Territory"
	IO = 106,
	//"Iraq"
	IQ = 107,
	//"Iran, Islamic Republic of"
	IR = 108,
	//"Iceland"
	IS = 109,
	//"Italy"
	IT = 110,
	//"Jersey"
	JE = 111,
	//"Jamaica"
	JM = 112,
	//"Jordan"
	JO = 113,
	//"Japan"
	JP = 114,
	//"Kenya"
	KE = 115,
	//"Kyrgyzstan"
	KG = 116,
	//"Cambodia"
	KH = 117,
	//"Kiribati"
	KI = 118,
	//"Comoros"
	KM = 119,
	//"Saint Kitts and Nevis"
	KN = 120,
	//"Korea, Democratic People's Republic of"
	KP = 121,
	//"Korea, Republic of"
	KR = 122,
	//"Kuwait"
	KW = 123,
	//"Cayman Islands"
	KY = 124,
	//"Kazakhstan"
	KZ = 125,
	//"Lao People's Democratic Republic"
	LA = 126,
	//"Lebanon"
	LB = 127,
	//"Saint Lucia"
	LC = 128,
	//"Liechtenstein"
	LI = 129,
	//"Sri Lanka"
	LK = 130,
	//"Liberia"
	LR = 131,
	//"Lesotho"
	LS = 132,
	//"Lithuania"
	LT = 133,
	//"Luxembourg"
	LU = 134,
	//"Latvia"
	LV = 135,
	//"Libya"
	LY = 136,
	//"Morocco"
	MA = 137,
	//"Monaco"
	MC = 138,
	//"Moldova, Republic of"
	MD = 139,
	//"Montenegro"
	ME = 140,
	//"Saint Martin (French part)"
	MF = 141,
	//"Madagascar"
	MG = 142,
	//"Marshall Islands"
	MH = 143,
	//"North Macedonia"
	MK = 144,
	//"Mali"
	ML = 145,
	//"Myanmar"
	MM = 146,
	//"Mongolia"
	MN = 147,
	//"Macao"
	MO = 148,
	//"Northern Mariana Islands"
	MP = 149,
	//"Martinique"
	MQ = 150,
	//"Mauritania"
	MR = 151,
	//"Montserrat"
	MS = 152,
	//"Malta"
	MT = 153,
	//"Mauritius"
	MU = 154,
	//"Maldives"
	MV = 155,
	//"Malawi"
	MW = 156,
	//"Mexico"
	MX = 157,
	//"Malaysia"
	MY = 158,
	//"Mozambique"
	MZ = 159,
	//"Namibia"
	NA = 160,
	//"New Caledonia"
	NC = 161,
	//"Niger"
	NE = 162,
	//"Norfolk Island"
	NF = 163,
	//"Nigeria"
	NG = 164,
	//"Nicaragua"
	NI = 165,
	//"Netherlands, Kingdom of the"
	NL = 166,
	//"Norway"
	NO = 167,
	//"Nepal"
	NP = 168,
	//"Nauru"
	NR = 169,
	//"Niue"
	NU = 170,
	//"New Zealand"
	NZ = 171,
	//"Oman"
	OM = 172,
	//"Panama"
	PA = 173,
	//"Peru"
	PE = 174,
	//"French Polynesia"
	PF = 175,
	//"Papua New Guinea"
	PG = 176,
	//"Philippines"
	PH = 177,
	//"Pakistan"
	PK = 178,
	//"Poland"
	PL = 179,
	//"Saint Pierre and Miquelon"
	PM = 180,
	//"Pitcairn"
	PN = 181,
	//"Puerto Rico"
	PR = 182,
	//"Palestine, State of"
	PS = 183,
	//"Portugal"
	PT = 184,
	//"Palau"
	PW = 185,
	//"Paraguay"
	PY = 186,
	//"Qatar"
	QA = 187,
	//"Réunion"
	RE = 188,
	//"Romania"
	RO = 189,
	//"Serbia"
	RS = 190,
	//"Russian Federation"
	RU = 191,
	//"Rwanda"
	RW = 192,
	//"Saudi Arabia"
	SA = 193,
	//"Solomon Islands"
	SB = 194,
	//"Seychelles"
	SC = 195,
	//"Sudan"
	SD = 196,
	//"Sweden"
	SE = 197,
	//"Singapore"
	SG = 198,
	//"Saint Helena, Ascension and Tristan da Cunha"
	SH = 199,
	//"Slovenia"
	SI = 200,
	//"Svalbard and Jan Mayen"
	SJ = 201,
	//"Slovakia"
	SK = 202,
	//"Sierra Leone"
	SL = 203,
	//"San Marino"
	SM = 204,
	//"Senegal"
	SN = 205,
	//"Somalia"
	SO = 206,
	//"Suriname"
	SR = 207,
	//"South Sudan"
	SS = 208,
	//"Sao Tome and Principe"
	ST = 209,
	//"El Salvador"
	SV = 210,
	//"Sint Maarten (Dutch part)"
	SX = 211,
	//"Syrian Arab Republic"
	SY = 212,
	//"Eswatini"
	SZ = 213,
	//"Turks and Caicos Islands"
	TC = 214,
	//"Chad"
	TD = 215,
	//"French Southern Territories"
	TF = 216,
	//"Togo"
	TG = 217,
	//"Thailand"
	TH = 218,
	//"Tajikistan"
	TJ = 219,
	//"Tokelau"
	TK = 220,
	//"Timor-Leste"
	TL = 221,
	//"Turkmenistan"
	TM = 222,
	//"Tunisia"
	TN = 223,
	//"Tonga"
	TO = 224,
	//"Türkiye"
	TR = 225,
	//"Trinidad and Tobago"
	TT = 226,
	//"Tuvalu"
	TV = 227,
	//"Taiwan, Province of China"
	TW = 228,
	//"Tanzania, United Republic of"
	TZ = 229,
	//"Ukraine"
	UA = 230,
	//"Uganda"
	UG = 231,
	//"United States Minor Outlying Islands"
	UM = 232,
	//"United States of America"
	US = 233,
	//"Uruguay"
	UY = 234,
	//"Uzbekistan"
	UZ = 235,
	//"Holy See"
	VA = 236,
	//"Saint Vincent and the Grenadines"
	VC = 237,
	//"Venezuela, Bolivarian Republic of"
	VE = 238,
	//"Virgin Islands (British)"
	VG = 239,
	//"Virgin Islands (U.S.)"
	VI = 240,
	//"Viet Nam"
	VN = 241,
	//"Vanuatu"
	VU = 242,
	//"Wallis and Futuna"
	WF = 243,
	//"Samoa"
	WS = 244,
	//"Yemen"
	YE = 245,
	//"Mayotte"
	YT = 246,
	//"South Africa"
	ZA = 247,
	//"Zambia"
	ZM = 248,
	//"Zimbabwe"
	ZW = 249
}
export enum EntityDeletionModeEnum {
	HardDelete = 0,
	SoftDelete = 1,
	BothDeleteModes = 2
}
